"use client";

import { useState } from "react";
import { useGateHold } from "@/app/components/Gate";
import { Detail } from "./characterViews";
import PlayEvents from "./PlayEvents";
import PlayProfile from "./PlayProfile";
import PlayNow, { hpColor } from "./PlayNow";
import PlayStatus from "./PlayStatus";
import PlayInventory, { EquipAction, EquipWhere } from "./PlayInventory";
import PlaySkills from "./PlaySkills";
import PlayRelations from "./PlayRelations";

type Tab = "profile" | "status" | "inventory" | "skills" | "relations" | "events";

const TABS: { tab: Tab; label: string }[] = [
  { tab: "profile", label: "プロフィール" },
  { tab: "status", label: "ステータス" },
  { tab: "inventory", label: "インベントリ" },
  { tab: "skills", label: "スキル" },
  { tab: "relations", label: "関係" },
  { tab: "events", label: "イベント" },
];

export interface PartyMember {
  slug: string;
  name: string;
  hp: { current: number; max: number } | null;
  pools: { label: string; color?: string | null; current: number; max: number }[];
  /** 同行者なら持ち主の PC のスラッグ */
  owner?: string;
  /** 仲間入りしたキャラの作成待ち。名だけを出し、中身は開かない */
  recruiting?: boolean;
}

function ratio(current: number, max: number): string {
  if (max <= 0) return "0%";
  return `${Math.max(0, Math.min(100, Math.round((current / max) * 100)))}%`;
}

/**
 * キャラ情報の画面。自分と同行者の一覧を出し、選んだキャラの中身をタブで見る。
 * 中身は GET /api/character/detail の応答。読み込みは親が担い、開いたキャラの分だけ渡す
 */
export default function PlayCharacter({
  region,
  party,
  detail,
  loading,
  message,
  opened,
  onOpened,
  onOpen,
  onLoadout,
  onGrowth,
  onEquip,
}: {
  /** リージョンの名。イベントのタブが読む */
  region: string;
  party: PartyMember[];
  detail: Detail | null;
  loading: boolean;
  message: string;
  /** 開いているキャラのスラッグ。一覧を出している間は null。親が持ち、替わるたびにロード画面を挟む */
  opened: string | null;
  /** 開くキャラを替えるとき。null は一覧へ戻る */
  onOpened: (slug: string | null) => void;
  /** 一覧でキャラを押したとき。中身を読む */
  onOpen: (slug: string) => void;
  /** 戦闘に持っていくスキルを決めたとき。失敗の文を返す */
  onLoadout: (slug: string, loadout: string[]) => Promise<string | null>;
  /** 自分の振り分けポイントを振ったとき。失敗の文を返す。同行者の分はラキバチが振る */
  onGrowth: (slug: string, spend: Record<string, number>) => Promise<string | null>;
  /** 装備の付け替えと持ち物の破棄を決めたとき。自分と同行者の分。where は着ける先の層。失敗の文を返す */
  onEquip: (slug: string, action: EquipAction, id?: string, where?: EquipWhere) => Promise<string | null>;
}) {
  const [tab, setTab] = useState<Tab>("profile");

  const member = party.find((one) => one.slug === opened) ?? null;
  // 開いたキャラの中身を読み終えるまで、進行の画面のロード画面に待ってもらう
  useGateHold(member !== null && !detail && loading);

  if (member) {
    return (
      <div className="pl-tabbed">
        <div className="pl-title">
          <h2>{detail?.name ?? member.name}</h2>
          <button type="button" className="pl-back" onClick={() => onOpened(null)}>
            戻る
          </button>
        </div>

        <div className="pl-tabs pl-tabs-folder" role="tablist">
          {TABS.filter((one) => (one.tab === "events" ? !member.owner : one.tab === "relations" ? Boolean(member.owner) : true)).map((one) => (
            <button
              key={one.tab}
              type="button"
              role="tab"
              aria-selected={tab === one.tab}
              className={tab === one.tab ? "pl-tab pl-tab-on" : "pl-tab"}
              onClick={() => setTab(one.tab)}
            >
              {one.label}
            </button>
          ))}
        </div>

        <div className="pl-folder-body">
        {detail ? (
          <>
            {tab === "profile" ? <PlayProfile text={detail.profile ?? ""} /> : null}
            {tab === "status" && !detail.status ? <p className="review-p review-dim">ステータスを持たないキャラクターです。</p> : null}
            {tab === "status" ? <PlayNow detail={detail} /> : null}
            {tab === "status" && detail.status ? (
              <PlayStatus key={detail.slug} status={detail.status} growth={detail.growth} onSpend={member.owner ? undefined : (spend) => onGrowth(member.slug, spend)} />
            ) : null}
            {tab === "inventory" ? <PlayInventory detail={detail} onEquip={(action, id, where) => onEquip(member.slug, action, id, where)} /> : null}
            {tab === "relations" && member.owner ? <PlayRelations relations={detail.relations ?? []} /> : null}
            {tab === "events" && !member.owner ? <PlayEvents events={detail.events ?? []} /> : null}
            {tab === "skills" ? <PlaySkills key={detail.slug} detail={detail} onSave={(loadout) => onLoadout(member.slug, loadout)} /> : null}
          </>
        ) : loading ? null : (
          <p className="pl-note">{message || "キャラクターを読めません"}</p>
        )}
        </div>
      </div>
    );
  }

  return (
    <div className="pl-party">
      <div className="pl-title">
        <h2>キャラ情報</h2>
      </div>
      <div className="pl-party-list">
        {party.map((one) =>
          one.recruiting ? (
            <div key={one.slug} className="pl-card pl-party-card">
              <p className="pl-card-name">
                {one.name}
                <span className="pl-card-tag">作成中</span>
              </p>
            </div>
          ) : (
          <button
            key={one.slug}
            type="button"
            className="pl-card pl-card-btn pl-party-card"
            onClick={() => {
              setTab("profile");
              onOpened(one.slug);
              onOpen(one.slug);
            }}
          >
            <p className="pl-card-name">
              {one.name}
              {one.owner ? <span className="pl-card-tag">同行者</span> : null}
            </p>
            {one.hp ? (
              <>
                <p className="pl-card-line">
                  HP {one.hp.current} / {one.hp.max}
                  {one.pools.map((pool) => ` ・ ${pool.label} ${pool.current} / ${pool.max}`).join("")}
                </p>
                <div className="pl-gauge">
                  <i style={{ width: ratio(one.hp.current, one.hp.max), background: hpColor(one.hp.current, one.hp.max) }} />
                </div>
                {one.pools.map((pool) => (
                  <div className="pl-gauge pl-gauge-mp" key={pool.label}>
                    <i style={{ width: ratio(pool.current, pool.max), ...(pool.color ? { background: pool.color } : {}) }} />
                  </div>
                ))}
              </>
            ) : null}
          </button>
          )
        )}
      </div>
    </div>
  );
}
